import { UserProfile } from '../user-profile';

/**
 * Where a skill's claim comes from.
 *
 * Ruled 2026-08-26 for both spec 04 section 5 and spec 05 section 3: the Skills
 * column and the Skills dimension both stay, on the condition that every skill
 * shows its provenance.
 *
 * Jackson's concern (item 1) is that the O*NET technical skills the product
 * aligns on are hyper-specific and often wrong for the person. That concern is
 * not resolved by keeping the column; what makes it safe is that a skill read
 * off the crosswalk and a skill confirmed by the member's own record stop
 * looking like the same claim. A member finding four skills they have never had
 * is the failure mode; a member seeing which four are inferred is not.
 *
 * This lives in one place because the occupation page and the plan both show it
 * and the spec asks for the same treatment on both. Two implementations of
 * "which of these did you actually earn" would eventually disagree.
 */
export interface HeldSkill {
  /** A string in the schema, on both Skill and Competency. */
  skillId: string;
  label: string;
  /** What said so: a job code, a credential, a transcript. */
  sources: string[];
}

export interface SkillWithProvenance {
  skillId: string;
  label: string;
  held: boolean;
  /**
   * OCC.4, 28 Sep 2026. Nothing on the record could be checked against this
   * row, so it is neither held nor a gap. Optional: only the Goals Report
   * skills card sets it.
   */
  notCompared?: boolean;
  sources: string[];
}

/**
 * WHAT THE OCCUPATION ASKS OF A PERSON, WHICH IS NOT ITS SOFTWARE LIST.
 *
 * PWS round 4, Jackson's item 11. Every readiness and alignment surface read
 * `occupation.skills`, which is the O*NET TECHNOLOGY SKILLS column: software
 * products, out of `api.onet_technical_skills`. That is why the Readiness
 * Assessment listed "3M Post-it App", "Adobe Acrobat" and "ADP Workforce Now"
 * as the skills a member needed for an occupation, and why it credited members
 * with owning them. His instruction is literal: do not show technology skills
 * anywhere, under any circumstances.
 *
 * These rows are the replacement: the O*NET descriptor data the subgraph
 * exposes as `CPDOccupation.ksas`, which is the knowledge, skills, abilities
 * and work activities the occupation actually asks for. It covers 997 of the
 * 1,016 occupations; the 19 it does not are SOC major group 55 uniformed
 * military codes, which O*NET scores no part of.
 *
 * THEY SAY NOTHING ABOUT THE MEMBER. They carry O*NET importance and level,
 * which rank what the occupation asks for, and no per-member "this person has
 * it" linkage at all. Nothing built on these rows may draw a check against an
 * item, because there is nothing that substantiates one. The measured version
 * of the same four families is the SkillsDNA gap report, and that is the only
 * thing allowed to mark an item.
 */
export interface OccupationKsaRow {
  id: string;
  name: string;
  description?: string | null;
  /**
   * Lowercase snake_case as it arrives: `skill`, `knowledge`, `ability`,
   * `work_activity`. Never presentable as it stands, so every surface maps it
   * to a heading through `groupKsasByFamily`.
   *
   * `software` IS ALSO A REAL VALUE IN THIS COLUMN, and it is precisely the
   * banned content: software product names. It is dropped rather than
   * rendered, which is why the grouping below is an allowlist.
   */
  type: string;
  /** O*NET importance, 1.0 to 5.0. The server orders by this, descending. */
  importance?: number | null;
  /** O*NET level, 0.0 to 7.0. */
  level?: number | null;
}

/**
 * A NARROW STRUCTURAL READ, AND WHY IT IS ONE.
 *
 * `ksas` lives on the subgraph's `CPDOccupation` type and in
 * `apps/cpd/graphql/generated.ts`. The two hand-written front end shapes the
 * consumers are typed against are `OccupationDetail`, which now declares it,
 * and `OccupationForExportPdf`, which does not yet. Both are produced by
 * casting the raw GraphQL result (`occupation.service.ts` does `as any` and
 * `as OccupationForExportPdf`), so the field is there at runtime the moment a
 * query selects it, whatever the interface says.
 *
 * Reading it structurally means one helper serves both shapes and neither has
 * to be edited to add a consumer. `readOccupationKsas` validates at runtime
 * because the field is genuinely absent until a query selects it, and an
 * occupation page must not throw over that.
 */
export interface OccupationWithKsas {
  ksas?: OccupationKsaRow[] | null;
}

/** One family of KSA rows, with the heading a person reads. */
export interface KsaFamily {
  /** The normalized family, which is also the `track` key. */
  key: string;
  heading: string;
  items: OccupationKsaRow[];
}

/**
 * The four families, in the order they are read in.
 *
 * Knowledge, then skills, then abilities, then work activities: what you know,
 * what you can do with it, what you have to be capable of, and what the job
 * has you doing all day. Fixed, because a list whose sections reorder between
 * two occupations cannot be compared.
 */
const KSA_FAMILIES: { key: string; heading: string; aliases: string[] }[] = [
  { key: 'knowledge', heading: 'Knowledge', aliases: ['knowledge'] },
  { key: 'skill', heading: 'Skills', aliases: ['skill', 'skills'] },
  { key: 'ability', heading: 'Abilities', aliases: ['ability', 'abilities'] },
  {
    key: 'work-activity',
    heading: 'Work Activities',
    // Snake case is what arrives. The rest are tolerated because the value
    // has already been described to this code two different ways.
    aliases: [
      'work_activity',
      'work_activities',
      'work activity',
      'work activities',
      'workactivity'
    ]
  }
];

/**
 * The occupation's KSA rows, or none.
 *
 * Typed `unknown` on purpose: the callers hold an `OccupationDetail` or an
 * `OccupationForExportPdf`, neither of which declares `ksas`, and a parameter
 * whose only property is optional would reject both outright.
 *
 * A row with no id or no name is dropped rather than rendered. `id` is the
 * `track` key on every list built from these, and a blank one takes the whole
 * list down with it.
 */
export function readOccupationKsas(occupation: unknown): OccupationKsaRow[] {
  const rows = (occupation as OccupationWithKsas | null | undefined)?.ksas;

  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.filter((row) => !!row?.id && !!row?.name);
}

/**
 * KSA rows grouped into their families, in reading order.
 *
 * AN ALLOWLIST, NOT A SWITCH WITH A DEFAULT BRANCH. `software` is a real
 * `element_type` in this column and it is exactly the content Jackson banned:
 * software product names offered to a member as capabilities. A grouping that
 * passed unrecognized types through, or that headed them with the raw value,
 * would put "Adobe Acrobat" back on the page under a new heading. So anything
 * that is not one of the four families is dropped, and a fifth family we do
 * want has to be added here deliberately.
 *
 * A family with no rows is left out rather than drawn as an empty heading.
 *
 * ORDER INSIDE A FAMILY IS THE ORDER THE ROWS ARRIVED IN, WHICH IS DELIBERATE.
 * The server returns them by O*NET importance descending, then name, which is
 * Jackson's instruction: "pull the importance on a skill for a civilian
 * occupation then rank them in descending order". Re-sorting here, including
 * alphabetically, would throw that ranking away.
 */
export function groupKsasByFamily(rows: OccupationKsaRow[]): KsaFamily[] {
  const grouped = new Map<string, KsaFamily>();

  (rows ?? []).forEach((row) => {
    const normalized = (row.type ?? '').trim().toLowerCase();
    const family = KSA_FAMILIES.find((candidate) =>
      candidate.aliases.includes(normalized)
    );

    if (!family) {
      return;
    }

    const group = grouped.get(family.key) ?? {
      key: family.key,
      heading: family.heading,
      items: []
    };
    group.items.push(row);
    grouped.set(family.key, group);
  });

  return KSA_FAMILIES.map((family) => grouped.get(family.key)).filter(
    (group): group is KsaFamily => !!group
  );
}

/**
 * What the member holds, and what said so.
 *
 * A competency can arrive from more than one source, and that is worth keeping:
 * two sources is a stronger claim than one. The same skill is merged rather
 * than skipped and its sources accumulate.
 */
export function buildHeldSkills(profile: UserProfile | null): HeldSkill[] {
  const bySkillId = new Map<string, HeldSkill>();

  const add = (competency, sourceTitle: string): void => {
    if (!competency?.skillId) {
      return;
    }

    const existing = bySkillId.get(competency.skillId);

    if (existing) {
      if (sourceTitle && !existing.sources.includes(sourceTitle)) {
        existing.sources.push(sourceTitle);
      }

      return;
    }

    bySkillId.set(competency.skillId, {
      skillId: competency.skillId,
      label: competency.competencyLabel,
      sources: sourceTitle ? [sourceTitle] : []
    });
  };

  profile?.userCompetencies?.sourceExperience?.forEach((sourceExperience) => {
    sourceExperience.advancedCompetencies?.forEach((competency) =>
      add(competency, sourceExperience.title)
    );
    sourceExperience.coreCompetencies?.forEach((competency) =>
      add(competency, sourceExperience.title)
    );
  });

  return [...bySkillId.values()];
}

/**
 * The occupation's skills, each marked with whether the member holds it and
 * what said so.
 */
export function withProvenance(
  occupationSkills: { skillId?: string; label?: string }[],
  held: HeldSkill[]
): SkillWithProvenance[] {
  return (occupationSkills ?? []).map((skill) => {
    const match = held.find((h) => h.skillId === skill.skillId);

    return {
      skillId: skill.skillId,
      label: skill.label,
      held: !!match,
      sources: match?.sources ?? []
    };
  });
}

/**
 * The provenance line under a skill.
 *
 * Never a score and never a percentage: both specs are explicit that this must
 * not look more precise than it is.
 *
 * Before the survey is done the line says so rather than saying nothing
 * confirms the skill. "You do not hold this" is not something the product knows
 * yet, so it must not say it.
 *
 * The two unheld lines used to open with "From the O*NET list for this
 * occupation", which was written when the only list was the O*NET technology
 * skills column. That column is gone from every surface (Jackson's item 11),
 * and this line is now only ever drawn against a measured element, so naming
 * O*NET here would name the wrong source.
 *
 * ONLY EVER CALLED FOR AN ITEM SOMETHING HAS ACTUALLY MEASURED. A descriptive
 * KSA row has no held state at all, and the surfaces that list those draw no
 * provenance line rather than passing `held: false` through here, which would
 * read as "we looked and you do not have it".
 */
export function describeProvenance(
  skill: Pick<SkillWithProvenance, 'held' | 'sources'>,
  isSkillsSurveyComplete: boolean
): string {
  if (!isSkillsSurveyComplete) {
    return "From this occupation's own profile. Finish the Skills survey to see whether you hold it.";
  }

  if (!skill.held) {
    return "From this occupation's own profile. Nothing in your record confirms it yet.";
  }

  return skill.sources.length
    ? `Confirmed by ${skill.sources.join(', ')}.`
    : 'Confirmed by your record.';
}

/**
 * "8 of the 12 skills this role lists".
 *
 * A count and not a percentage, which is the whole of spec 05's `scale` field:
 * the count can be checked against the list below it, and a percentage cannot.
 */
export function skillScale(skills: SkillWithProvenance[]): string {
  const total = (skills ?? []).length;

  if (!total) {
    // Was "This role does not list technical skills". Technical skills are not
    // something any surface lists any more (Jackson's item 11), so the
    // sentence has to be about the role's own profile instead.
    return 'This role does not publish what it asks of you yet.';
  }

  const held = skills.filter((skill) => skill.held).length;

  return `${held} of the ${total} skills this role lists`;
}
